import {
  HistoryManager,
  HistoryOperation,
  HistorySnapshot,
  HistoryState,
  HistoryConfig,
  HistoryEvent,
  HistoryEventCallback,
  HistoryEventData,
  HistoryExport,
  HistoryDiff
} from '../types/history-interfaces';

/**
 * Реализация менеджера истории изменений
 */
export class HistoryManagerImpl implements HistoryManager {
  private operations: HistoryOperation[] = [];
  private snapshots: HistorySnapshot[] = [];
  private currentIndex: number = -1;
  private config: Required<HistoryConfig>;
  private eventListeners: Map<HistoryEvent, Set<HistoryEventCallback>> = new Map();
  private stats = {
    undoCount: 0,
    redoCount: 0,
    snapshotCount: 0,
    lastCleanup: Date.now()
  };

  constructor(config: HistoryConfig = {}) {
    this.config = {
      maxOperations: config.maxOperations ?? 100,
      maxSize: config.maxSize ?? 10 * 1024 * 1024, // 10MB
      snapshotInterval: config.snapshotInterval ?? 10,
      autoCleanup: config.autoCleanup ?? true,
      compression: config.compression ?? false,
      trackOperations: config.trackOperations ?? [],
      ignoreOperations: config.ignoreOperations ?? []
    };

    // Инициализация карты событий
    const events: HistoryEvent[] = [
      'operation-added', 'snapshot-created', 'undo-performed',
      'redo-performed', 'history-cleared', 'cleanup-performed'
    ];
    events.forEach(event => {
      this.eventListeners.set(event, new Set());
    });
  }

  /**
   * Добавить операцию в историю
   */
  addOperation(operation: Omit<HistoryOperation, 'id' | 'timestamp'>): void {
    // Проверяем, нужно ли игнорировать операцию
    if (this.config.ignoreOperations.includes(operation.type)) {
      return;
    }

    // Проверяем, нужно ли отслеживать операцию
    if (this.config.trackOperations.length > 0 && 
        !this.config.trackOperations.includes(operation.type)) {
      return;
    }

    const newOperation: HistoryOperation = {
      ...operation,
      id: this.generateId(),
      timestamp: Date.now()
    };

    // Удаляем операции после текущей позиции (для новой ветки истории)
    if (this.currentIndex < this.operations.length - 1) {
      this.operations = this.operations.slice(0, this.currentIndex + 1);
    }

    this.operations.push(newOperation);
    this.currentIndex = this.operations.length - 1;

    // Создаем снимок если нужно
    if (this.shouldCreateSnapshot()) {
      this.createSnapshot(operation.after, `Snapshot after ${operation.description}`);
    }

    // Автоочистка если включена
    if (this.config.autoCleanup) {
      this.performCleanupIfNeeded();
    }

    this.emitEvent('operation-added', { 
      operation: newOperation, 
      state: this.getState(),
      timestamp: Date.now()
    });
  }

  /**
   * Создать снимок состояния
   */
  createSnapshot(data: any, description: string): HistorySnapshot {
    const snapshot: HistorySnapshot = {
      id: this.generateId(),
      timestamp: Date.now(),
      data: this.config.compression ? this.compressData(data) : this.deepClone(data),
      metadata: {
        operation: description,
        source: 'system'
      },
      size: this.calculateSize(data)
    };

    this.snapshots.push(snapshot);
    this.stats.snapshotCount++;

    this.emitEvent('snapshot-created', {
      snapshot,
      state: this.getState(),
      timestamp: Date.now()
    });

    return snapshot;
  }

  /**
   * Отменить последнюю операцию
   */
  async undo(): Promise<any> {
    if (!this.canPerformUndo()) {
      throw new Error('Невозможно выполнить отмену');
    }

    const operation = this.operations[this.currentIndex];
    this.currentIndex--;
    this.stats.undoCount++;

    const result = operation.before;

    this.emitEvent('undo-performed', {
      operation,
      state: this.getState(),
      timestamp: Date.now()
    });

    return result;
  }

  /**
   * Повторить отмененную операцию
   */
  async redo(): Promise<any> {
    if (!this.canPerformRedo()) {
      throw new Error('Невозможно выполнить повтор');
    }

    this.currentIndex++;
    const operation = this.operations[this.currentIndex];
    this.stats.redoCount++;

    const result = operation.after;

    this.emitEvent('redo-performed', {
      operation,
      state: this.getState(),
      timestamp: Date.now()
    });

    return result;
  }

  /**
   * Отменить до определенной операции
   */
  async undoTo(operationId: string): Promise<any> {
    const targetIndex = this.operations.findIndex(op => op.id === operationId);
    if (targetIndex === -1) {
      throw new Error(`Операция с ID ${operationId} не найдена`);
    }

    if (targetIndex >= this.currentIndex) {
      throw new Error('Невозможно отменить до указанной операции');
    }

    let result: any;
    while (this.currentIndex > targetIndex) {
      result = await this.undo();
    }

    return result;
  }

  /**
   * Повторить до определенной операции
   */
  async redoTo(operationId: string): Promise<any> {
    const targetIndex = this.operations.findIndex(op => op.id === operationId);
    if (targetIndex === -1) {
      throw new Error(`Операция с ID ${operationId} не найдена`);
    }

    if (targetIndex <= this.currentIndex) {
      throw new Error('Невозможно повторить до указанной операции');
    }

    let result: any;
    while (this.currentIndex < targetIndex) {
      result = await this.redo();
    }

    return result;
  }

  /**
   * Получить текущее состояние истории
   */
  getState(): HistoryState {
    const totalSize = this.calculateTotalSize();
    
    return {
      currentIndex: this.currentIndex,
      maxSize: this.config.maxOperations,
      totalOperations: this.operations.length,
      totalSize,
      canUndo: this.canPerformUndo(),
      canRedo: this.canPerformRedo(),
      stats: { ...this.stats }
    };
  }

  /**
   * Получить список операций
   */
  getOperations(limit?: number): HistoryOperation[] {
    const ops = [...this.operations];
    return limit ? ops.slice(-limit) : ops;
  }

  /**
   * Получить список снимков
   */
  getSnapshots(limit?: number): HistorySnapshot[] {
    const snapshots = [...this.snapshots];
    return limit ? snapshots.slice(-limit) : snapshots;
  }

  /**
   * Очистить историю
   */
  clear(): void {
    this.operations = [];
    this.snapshots = [];
    this.currentIndex = -1;
    this.stats = {
      undoCount: 0,
      redoCount: 0,
      snapshotCount: 0,
      lastCleanup: Date.now()
    };

    this.emitEvent('history-cleared', {
      state: this.getState(),
      timestamp: Date.now()
    });
  }

  /**
   * Очистить старые записи
   */
  cleanup(): void {
    const beforeCount = this.operations.length;
    
    // Удаляем старые операции если превышен лимит
    if (this.operations.length > this.config.maxOperations) {
      const toRemove = this.operations.length - this.config.maxOperations;
      this.operations = this.operations.slice(toRemove);
      this.currentIndex = Math.max(-1, this.currentIndex - toRemove);
    }

    // Удаляем старые снимки если превышен размер
    const totalSize = this.calculateTotalSize();
    if (totalSize > this.config.maxSize) {
      this.cleanupBySize();
    }

    this.stats.lastCleanup = Date.now();

    if (beforeCount !== this.operations.length) {
      this.emitEvent('cleanup-performed', {
        state: this.getState(),
        timestamp: Date.now()
      });
    }
  }

  /**
   * Получить операцию по ID
   */
  getOperation(id: string): HistoryOperation | null {
    return this.operations.find(op => op.id === id) || null;
  }

  /**
   * Получить снимок по ID
   */
  getSnapshot(id: string): HistorySnapshot | null {
    return this.snapshots.find(snapshot => snapshot.id === id) || null;
  }

  /**
   * Экспорт истории
   */
  export(): HistoryExport {
    return {
      version: '1.0.0',
      timestamp: Date.now(),
      operations: [...this.operations],
      snapshots: [...this.snapshots],
      state: this.getState(),
      config: { ...this.config }
    };
  }

  /**
   * Импорт истории
   */
  import(data: HistoryExport): void {
    this.operations = [...data.operations];
    this.snapshots = [...data.snapshots];
    this.currentIndex = data.state.currentIndex;
    this.stats = { ...data.state.stats };

    this.emitEvent('history-cleared', {
      state: this.getState(),
      timestamp: Date.now()
    });
  }

  /**
   * Подписка на события истории
   */
  on(event: HistoryEvent, callback: HistoryEventCallback): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.add(callback);
    }
  }

  /**
   * Отписка от событий
   */
  off(event: HistoryEvent, callback: HistoryEventCallback): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
  }

  /**
   * Создать diff между двумя состояниями
   */
  createDiff(before: any, after: any, path: string[] = []): HistoryDiff[] {
    const diffs: HistoryDiff[] = [];

    if (before === after) {
      return diffs;
    }

    if (typeof before !== typeof after || before === null || after === null) {
      diffs.push({
        type: 'modified',
        path: [...path],
        oldValue: before,
        newValue: after
      });
      return diffs;
    }

    if (Array.isArray(before) && Array.isArray(after)) {
      const maxLength = Math.max(before.length, after.length);
      for (let i = 0; i < maxLength; i++) {
        if (i >= before.length) {
          diffs.push({
            type: 'added',
            path: [...path, i.toString()],
            newValue: after[i],
            index: i
          });
        } else if (i >= after.length) {
          diffs.push({
            type: 'removed',
            path: [...path, i.toString()],
            oldValue: before[i],
            index: i
          });
        } else {
          diffs.push(...this.createDiff(before[i], after[i], [...path, i.toString()]));
        }
      }
      return diffs;
    }

    if (typeof before === 'object' && typeof after === 'object') {
      const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
      for (const key of allKeys) {
        if (!(key in before)) {
          diffs.push({
            type: 'added',
            path: [...path, key],
            newValue: after[key]
          });
        } else if (!(key in after)) {
          diffs.push({
            type: 'removed',
            path: [...path, key],
            oldValue: before[key]
          });
        } else {
          diffs.push(...this.createDiff(before[key], after[key], [...path, key]));
        }
      }
      return diffs;
    }

    diffs.push({
      type: 'modified',
      path: [...path],
      oldValue: before,
      newValue: after
    });

    return diffs;
  }

  // Приватные методы

  private generateId(): string {
    return `hist_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private canPerformUndo(): boolean {
    return this.currentIndex >= 0 && 
           this.operations[this.currentIndex]?.canUndo !== false;
  }

  private canPerformRedo(): boolean {
    return this.currentIndex < this.operations.length - 1 && 
           this.operations[this.currentIndex + 1]?.canRedo !== false;
  }

  private shouldCreateSnapshot(): boolean {
    return this.operations.length % this.config.snapshotInterval === 0;
  }

  private performCleanupIfNeeded(): void {
    if (this.operations.length > this.config.maxOperations || 
        this.calculateTotalSize() > this.config.maxSize) {
      this.cleanup();
    }
  }

  private calculateSize(data: any): number {
    return new Blob([JSON.stringify(data)]).size;
  }

  private calculateTotalSize(): number {
    const operationsSize = this.operations.reduce((sum, op) => 
      sum + this.calculateSize(op), 0);
    const snapshotsSize = this.snapshots.reduce((sum, snapshot) => 
      sum + snapshot.size, 0);
    return operationsSize + snapshotsSize;
  }

  private cleanupBySize(): void {
    let currentSize = this.calculateTotalSize();
    
    // Сначала удаляем старые снимки
    while (currentSize > this.config.maxSize && this.snapshots.length > 1) {
      const removed = this.snapshots.shift();
      if (removed) {
        currentSize -= removed.size;
      }
    }

    // Затем удаляем старые операции
    while (currentSize > this.config.maxSize && this.operations.length > 10) {
      const removed = this.operations.shift();
      if (removed) {
        currentSize -= this.calculateSize(removed);
        this.currentIndex--;
      }
    }
  }

  private deepClone(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }
    if (Array.isArray(obj)) {
      return obj.map(item => this.deepClone(item));
    }
    const cloned: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloned[key] = this.deepClone(obj[key]);
      }
    }
    return cloned;
  }

  private compressData(data: any): any {
    // Простое сжатие - удаление undefined значений и дублирование
    return JSON.parse(JSON.stringify(data));
  }

  private emitEvent(event: HistoryEvent, data: HistoryEventData): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Ошибка в обработчике события ${event}:`, error);
        }
      });
    }
  }
} 